import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Globe, Smartphone, Monitor, Tablet, MapPin, RefreshCw, Users, Clock,
  TrendingUp, TrendingDown, Minus, Bot,
} from "lucide-react";
import { toast } from "sonner";

/* Global Traffic Dashboard — owner/admin only
 * Filter traffic by 24h / 7d / 30d / All. Auto-refreshes every 30s, shows
 * a compact SVG line chart of visits over the selected period, and pill
 * deltas comparing the current window vs the previous same-length window. */
const REFRESH_MS = 30000;

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d",  label: "7d"  },
  { key: "30d", label: "30d" },
  { key: "all", label: "All" },
];

const DEVICE_ICONS = {
  Desktop: Monitor,
  Mobile: Smartphone,
  Tablet: Tablet,
  Unknown: Globe,
};
const DEVICE_COLORS = {
  Desktop: "#D4AF37",
  Mobile: "#22D3EE",
  Tablet: "#A78BFA",
  Unknown: "#6B7280",
};

function flagEmoji(code) {
  if (!code || code.length !== 2 || code === "??" || code === "LO") return "🌐";
  const c = code.toUpperCase();
  return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}
function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function GlobalTrafficDashboard() {
  const [rangeKey, setRangeKey] = useState("7d");
  const [excludeBots, setExcludeBots] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (r, excludeBotsFlag, isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/traffic", {
        params: { range: r, exclude_bots: excludeBotsFlag },
      });
      setData(data);
    } catch (e) {
      if (isManual) toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  };

  useEffect(() => {
    load(rangeKey, excludeBots);
    const t = setInterval(() => load(rangeKey, excludeBots, false), REFRESH_MS);
    return () => clearInterval(t);
  }, [rangeKey, excludeBots]);

  if (loading) {
    return (
      <div className="bg-[#0c0c0c] border border-white/5 p-8 text-sm text-neutral-500" data-testid="traffic-loading">
        Loading Global Traffic Dashboard…
      </div>
    );
  }
  if (!data) return null;

  const maxCity = Math.max(1, ...data.top_cities.slice(0, 10).map((c) => c.visits));
  const maxCountry = Math.max(1, ...data.top_countries.slice(0, 10).map((c) => c.visits));

  return (
    <div className="mb-12" data-testid="global-traffic-dashboard">
      {/* Header + range toggle */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Globe size={16} className="text-amber-500" />
            <div className="text-overline">Global Traffic Dashboard</div>
          </div>
          <div className="text-xs text-neutral-500 font-mono-tech">
            {data.totals.countries_reached} countries · {data.totals.cities_reached} cities · {data.totals.unique_ips.toLocaleString()} unique IPs
            <span className="ml-2 text-neutral-700">
              · lifetime {data.totals.total_visits_all_time.toLocaleString()} visits
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-[#0c0c0c] border border-white/10" data-testid="range-toggle">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRangeKey(r.key)}
                data-testid={`range-${r.key}`}
                className={`px-3 py-2 text-xs uppercase tracking-widest font-semibold transition-colors ${
                  rangeKey === r.key
                    ? "bg-amber-500 text-black"
                    : "text-neutral-500 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setExcludeBots((v) => !v)}
            data-testid="bot-filter-toggle"
            title={excludeBots ? "Bots are hidden. Click to include." : "Bots are counted. Click to hide."}
            className={`inline-flex items-center gap-2 border px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
              excludeBots
                ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                : "border-red-500/40 text-red-300 hover:bg-red-500/10"
            }`}
          >
            <Bot size={12} />
            {excludeBots ? "Bots Hidden" : "Bots Counted"}
            {data?.bots?.in_window > 0 && (
              <span className="font-mono-tech text-[10px] text-neutral-500">
                · {data.bots.in_window.toLocaleString()} filtered
              </span>
            )}
          </button>
          <button
            onClick={() => load(rangeKey, excludeBots, true)}
            disabled={refreshing}
            data-testid="traffic-refresh-btn"
            className="inline-flex items-center gap-2 border border-white/10 px-3 py-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-white transition-colors disabled:opacity-40"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            Sync
          </button>
        </div>
      </div>

      {/* Overview strip with deltas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-4">
        <OverviewTile
          icon={Users}
          label={rangeKey === "all" ? "Visits (All Time)" : `Visits · ${rangeKey}`}
          value={data.totals.total_visits}
          delta={data.deltas?.visits?.delta_pct}
          testid="overview-visits"
        />
        <OverviewTile
          icon={Globe}
          label={rangeKey === "all" ? "Unique IPs (All Time)" : `Unique IPs · ${rangeKey}`}
          value={data.totals.unique_ips}
          delta={data.deltas?.unique_ips?.delta_pct}
          accent="#22D3EE"
          testid="overview-unique"
        />
        <OverviewTile icon={Clock} label="Last 24h" value={data.totals.visits_24h} accent="#A78BFA" testid="overview-24h" />
        <OverviewTile icon={Globe} label="Countries" value={data.totals.countries_reached} accent="#D4AF37" testid="overview-countries" />
      </div>

      {/* Line chart — visits over the selected range */}
      <LineChart timeseries={data.timeseries} rangeKey={rangeKey} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Device breakdown */}
        <div className="bg-[#0c0c0c] border border-white/5 p-5" data-testid="device-breakdown">
          <div className="text-overline mb-4">Device Breakdown · {rangeKey}</div>
          {data.device_breakdown.length === 0 ? (
            <div className="text-xs text-neutral-500">No device data in this window.</div>
          ) : (
            <>
              <div className="flex h-4 overflow-hidden bg-[#0a0a0a] border border-white/5 mb-4">
                {data.device_breakdown.map((d) => (
                  <div
                    key={d.device}
                    style={{ width: `${d.percent}%`, background: DEVICE_COLORS[d.device] || "#6B7280" }}
                    title={`${d.device}: ${d.count} (${d.percent}%)`}
                    data-testid={`device-bar-${d.device.toLowerCase()}`}
                  />
                ))}
              </div>
              <div className="space-y-2">
                {data.device_breakdown.map((d) => {
                  const Icon = DEVICE_ICONS[d.device] || Globe;
                  return (
                    <div key={d.device} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <Icon size={14} style={{ color: DEVICE_COLORS[d.device] || "#6B7280" }} />
                        <span className="text-white">{d.device}</span>
                      </div>
                      <span className="text-neutral-500 font-mono-tech">
                        {d.percent.toFixed(1)}% · {d.count.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Top cities */}
        <div className="bg-[#0c0c0c] border border-white/5 p-5" data-testid="top-cities">
          <div className="text-overline mb-4">Top Cities · {rangeKey}</div>
          {data.top_cities.length === 0 ? (
            <div className="text-xs text-neutral-500">No geolocated visits in this window.</div>
          ) : (
            <div className="space-y-2">
              {data.top_cities.slice(0, 10).map((c) => (
                <div key={`${c.country_code}-${c.city}`} className="flex items-center gap-3 text-xs" data-testid={`city-${c.city.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="text-lg leading-none">{flagEmoji(c.country_code)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate">{c.city}</div>
                    <div className="text-[10px] text-neutral-500 truncate">{c.country}</div>
                  </div>
                  <div className="w-24 h-1.5 bg-white/5 overflow-hidden">
                    <div className="h-full bg-amber-500" style={{ width: `${(c.visits / maxCity) * 100}%` }} />
                  </div>
                  <div className="w-10 text-right font-mono-tech text-amber-500">{c.visits}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Top countries */}
        <div className="bg-[#0c0c0c] border border-white/5 p-5" data-testid="top-countries">
          <div className="text-overline mb-4">Top Countries · {rangeKey}</div>
          {data.top_countries.length === 0 ? (
            <div className="text-xs text-neutral-500">No country data in this window.</div>
          ) : (
            <div className="space-y-2">
              {data.top_countries.slice(0, 10).map((c) => (
                <div key={c.country_code + c.country} className="flex items-center gap-3 text-xs" data-testid={`country-${c.country_code}`}>
                  <div className="text-lg leading-none">{flagEmoji(c.country_code)}</div>
                  <div className="flex-1 text-white truncate">{c.country}</div>
                  <div className="w-24 h-1.5 bg-white/5">
                    <div className="h-full bg-emerald-400" style={{ width: `${(c.visits / maxCountry) * 100}%` }} />
                  </div>
                  <div className="w-10 text-right font-mono-tech text-emerald-300">{c.visits}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Latest visits feed */}
        <div className="bg-[#0c0c0c] border border-white/5 p-5" data-testid="latest-visits">
          <div className="text-overline mb-4">Latest Visits · {rangeKey}</div>
          {data.latest_visits.length === 0 ? (
            <div className="text-xs text-neutral-500">No visits recorded in this window.</div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {data.latest_visits.map((v, idx) => {
                const Icon = DEVICE_ICONS[v.device] || Globe;
                return (
                  <div key={idx} className="flex items-center gap-3 text-xs border-b border-white/5 pb-2 last:border-0">
                    <div className="text-base">{flagEmoji(v.country_code)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white truncate flex items-center gap-1.5">
                        <MapPin size={10} className="text-neutral-500" />
                        {v.city} · <span className="text-neutral-500">{v.country}</span>
                      </div>
                    </div>
                    <Icon size={12} style={{ color: DEVICE_COLORS[v.device] || "#6B7280" }} />
                    <div className="text-[10px] text-neutral-500 font-mono-tech">{timeAgo(v.last_seen_at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 text-[10px] text-neutral-600 font-mono-tech">
        Auto-refresh every 30s · Geo powered by ip-api.com · Cached per-IP for 24h ·
        {excludeBots ? " Hiding" : " Counting"} bots ({data?.bots?.all_time?.toLocaleString() || 0} known lifetime)
      </div>
    </div>
  );
}

function DeltaPill({ delta }) {
  if (delta === null || delta === undefined) return null;
  const positive = delta > 0;
  const neutral = delta === 0;
  const Icon = neutral ? Minus : positive ? TrendingUp : TrendingDown;
  const color = neutral ? "#6B7280" : positive ? "#10B981" : "#E11D48";
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono-tech font-semibold px-1.5 py-0.5"
      style={{ color, border: `1px solid ${color}55` }}
      data-testid="delta-pill"
    >
      <Icon size={9} />
      {positive ? "+" : ""}{delta}%
    </span>
  );
}

function OverviewTile({ icon: Icon, label, value, delta, accent = "#D4AF37", testid }) {
  return (
    <div className="bg-[#0c0c0c] p-4" data-testid={testid}>
      <Icon size={14} style={{ color: accent }} className="mb-2" strokeWidth={1.5} />
      <div className="font-display text-2xl font-medium text-white flex items-baseline gap-2">
        {Number(value || 0).toLocaleString()}
        <DeltaPill delta={delta} />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

/* SVG line + area chart, no dependencies. */
function LineChart({ timeseries, rangeKey }) {
  if (!timeseries || timeseries.length === 0) {
    return (
      <div className="bg-[#0c0c0c] border border-white/5 p-5 text-xs text-neutral-500" data-testid="traffic-linechart">
        No timeseries data yet.
      </div>
    );
  }
  const W = 800;
  const H = 160;
  const PAD_L = 40;
  const PAD_R = 12;
  const PAD_T = 16;
  const PAD_B = 22;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const max = Math.max(1, ...timeseries.map((t) => t.visits));
  const total = timeseries.reduce((s, t) => s + t.visits, 0);
  const peak = timeseries.reduce((a, b) => (b.visits > a.visits ? b : a), timeseries[0]);

  const points = timeseries.map((t, i) => {
    const x = PAD_L + (i / Math.max(1, timeseries.length - 1)) * innerW;
    const y = PAD_T + innerH - (t.visits / max) * innerH;
    return { x, y, ...t };
  });
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaPath =
    linePath +
    ` L${points[points.length - 1].x.toFixed(1)},${(PAD_T + innerH).toFixed(1)}` +
    ` L${points[0].x.toFixed(1)},${(PAD_T + innerH).toFixed(1)} Z`;

  // ~5 label ticks
  const step = Math.max(1, Math.floor(points.length / 5));
  return (
    <div className="bg-[#0c0c0c] border border-white/5 p-5" data-testid="traffic-linechart">
      <div className="flex items-center justify-between mb-3 text-xs">
        <div className="text-overline">Visits Timeline · {rangeKey}</div>
        <div className="text-neutral-500 font-mono-tech">
          {total.toLocaleString()} visits · peak {peak.visits} @ {peak.label}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none">
        <defs>
          <linearGradient id="trafficAreaGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Y grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((r) => (
          <line
            key={r}
            x1={PAD_L} x2={W - PAD_R}
            y1={PAD_T + innerH * (1 - r)} y2={PAD_T + innerH * (1 - r)}
            stroke="#ffffff10"
          />
        ))}
        {/* Y labels */}
        {[0, 0.5, 1].map((r) => (
          <text
            key={r}
            x={PAD_L - 6}
            y={PAD_T + innerH * (1 - r) + 3}
            textAnchor="end"
            fill="#6B7280"
            fontSize="9"
            fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace"
          >
            {Math.round(max * r).toLocaleString()}
          </text>
        ))}
        {/* Area + line */}
        <path d={areaPath} fill="url(#trafficAreaGrad)" />
        <path d={linePath} fill="none" stroke="#D4AF37" strokeWidth="1.5" />
        {/* Points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={p.visits === peak.visits ? 3 : 1.5}
            fill={p.visits === peak.visits ? "#D4AF37" : "#0c0c0c"} stroke="#D4AF37" strokeWidth="1">
            <title>{`${p.label}: ${p.visits}`}</title>
          </circle>
        ))}
        {/* X labels */}
        {points.map((p, i) =>
          i % step === 0 || i === points.length - 1 ? (
            <text
              key={`xl-${i}`}
              x={p.x}
              y={H - 6}
              textAnchor="middle"
              fill="#6B7280"
              fontSize="9"
              fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace"
            >
              {p.label}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
}
